/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useLayoutEffect, useRef } from "react";
import { PolicyPopupLink } from "@/components/common/PolicyPopupLink";
import { Link } from "react-router-dom";
import "../../static/css/common/footer.css";
import { useTranslation } from "react-i18next";
import { AddLangToPath } from "@/helper/AddLanguagePath";
import { useWindowWidth } from "@/hooks/useWindowWidth";
import {
  SEPARATOR_CLASS,
  markLineEdgeSeparators,
} from "@/utils/lineEdgeSeparators";

/**
 * The "|" between two footer links. Its own element, so that on a phone, where
 * the links wrap, one that would start or end a line can be hidden
 * (utils/lineEdgeSeparators.ts). Hidden from screen readers, which announce
 * the links as a list of links already.
 */
const Separator: React.FC = () => (
  <span className={SEPARATOR_CLASS} aria-hidden="true">
    |
  </span>
);

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const windowWidth = useWindowWidth(1000);
  const contentRef = useRef<HTMLDivElement>(null);

  const isDesktopDevice = windowWidth >= 1024;
  const showDownloadLink = windowWidth > 1000;

  // Re-measure whenever the row can re-wrap: on mount, when the window or the
  // row changes size, and when the links or their language change.
  useLayoutEffect(() => {
    const row = contentRef.current;
    if (!row) return undefined;
    const update = () => {
      markLineEdgeSeparators(row);
    };
    update();
    window.addEventListener("resize", update);
    const observer =
      typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(row);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [showDownloadLink, t]);

  return (
    <footer className="footer">
      <div className="footer-content" ref={contentRef}>
        <span>
          <Link className="footer-text" to={AddLangToPath("/")}>
            {t("footer.home")}
          </Link>
        </span>
        {showDownloadLink && (
          <>
            {" "}
            <Separator />{" "}
            <span>
              <Link className="footer-text" to={AddLangToPath("/download/")}>
                {t("footer.download")}
              </Link>
            </span>
          </>
        )}{" "}
        <Separator />{" "}
        <span>
          <Link className="footer-text" to={AddLangToPath("/product-support/")}>
            {t("footer.support")}
          </Link>
        </span>{" "}
        <Separator />{" "}
        <span>
          <Link className="footer-text" to={AddLangToPath("/about-deskshare/")}>
            {t("footer.about")}
          </Link>
        </span>{" "}
        <Separator />{" "}
        <span>
          <PolicyPopupLink
            path="/privacy-policy/"
            label={t("footer.privacy_policy")}
            isDesktopDevice={isDesktopDevice}
          />
        </span>{" "}
        <Separator />{" "}
        <span>
          <PolicyPopupLink
            path="/refund-policy/"
            label={t("footer.refund_policy")}
            isDesktopDevice={isDesktopDevice}
          />
        </span>{" "}
        <Separator />{" "}
        <span>
          <Link className="footer-text" to={AddLangToPath("/contact-us/")}>
            {t("footer.contact_us")}
          </Link>
        </span>{" "}
        <Separator />{" "}
        <span>
          {/* Session 515 review, N-1: the privacy policy promises a Cookie
            Settings link "at the bottom of any page", and withdrawing consent
            must be as easy as giving it. Only the home page and My PC Info had
            one; every other page draws this footer. CookieConsent listens for
            the event and opens its preferences panel. */}
          <button
            type="button"
            className="footer-text footer-cookie-button"
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent("ds:show-cookie-preferences"),
              )
            }
          >
            {t("footer.cookie_settings")}
          </button>
        </span>
      </div>

      <div className="end">{t("footer.copyrite")}</div>
    </footer>
  );
};

export default Footer;
